import type { ReactElement } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { PartnerVisibilityStrings } from '@/i18n/partnerVisibility';
import type { PartnerVisibilityStore } from '@/lib/api/types';

export interface WithdrawPartnerVisibilityDialogProps {
  store: PartnerVisibilityStore;
  strings: PartnerVisibilityStrings;
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (storeId: number) => void;
  trigger?: ReactElement;
  returnFocusElement?: HTMLElement | null;
  fallbackFocusElement?: HTMLElement | null;
}

export function WithdrawPartnerVisibilityDialog({
  store,
  strings,
  open,
  pending,
  onOpenChange,
  onConfirm,
  trigger,
  returnFocusElement,
  fallbackFocusElement,
}: WithdrawPartnerVisibilityDialogProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (pending && !nextOpen) {
      return;
    }

    onOpenChange(nextOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      ) : null}
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          if (!returnFocusElement && !fallbackFocusElement) {
            return;
          }

          event.preventDefault();
          queueMicrotask(() => {
            const canRestoreToTrigger =
              returnFocusElement?.isConnected &&
              !returnFocusElement.matches(':disabled, [aria-disabled="true"]');
            const focusTarget = canRestoreToTrigger
              ? returnFocusElement
              : fallbackFocusElement?.isConnected
                ? fallbackFocusElement
                : null;

            focusTarget?.focus();
          });
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{strings.withdrawConfirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>
            {strings.withdrawConfirmBody}
            {' '}
            <span className="mt-2 block font-medium text-foreground" dir="auto">
              {store.store_name}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            {strings.withdrawConfirmCancel}
          </AlertDialogCancel>
          <AlertDialogAction
            aria-busy={pending || undefined}
            disabled={pending}
            onClick={() => {
              if (!pending) {
                onConfirm(store.store_id);
              }
            }}
          >
            {strings.withdrawConfirmAction}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
